<script setup>
import {
    NCard, NSpace, NH1, NH2, NH3, NP, NAlert, NTable,
    NDivider, NList, NListItem, NGradientText, NButton, NSwitch
} from 'naive-ui';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Cookie, Settings, Shield, Info, CheckCircle } from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

const lastUpdated = "January 1, 2025";

// Cookie preferences
const cookiePreferences = ref({
    necessary: true, // Always enabled
    functional: true,
    analytics: false,
    marketing: false
});

const saveCookiePreferences = () => {
    // Save to localStorage or send to backend
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences.value));
    window.$message?.success(dt('cookies.preferences.saved'));
};

const cookieCategories = [
    {
        key: 'necessary',
        name: 'cookies.categories.necessary.name',
        description: 'cookies.categories.necessary.description',
        icon: Shield,
        color: '#36d1dc',
        canDisable: false,
        examples: 'cookies.categories.necessary.examples'
    },
    {
        key: 'functional',
        name: 'cookies.categories.functional.name',
        description: 'cookies.categories.functional.description',
        icon: Settings,
        color: '#5b86e5',
        canDisable: true,
        examples: 'cookies.categories.functional.examples'
    },
    {
        key: 'analytics',
        name: 'cookies.categories.analytics.name',
        description: 'cookies.categories.analytics.description',
        icon: Info,
        color: '#f7b733',
        canDisable: true,
        examples: 'cookies.categories.analytics.examples'
    },
    {
        key: 'marketing',
        name: 'cookies.categories.marketing.name',
        description: 'cookies.categories.marketing.description',
        icon: CheckCircle,
        color: '#ef473a',
        canDisable: true,
        examples: 'cookies.categories.marketing.examples'
    }
];
</script>

<template>
    <div class="cookies-container">
        <!-- Hero Section -->
        <div class="hero-section">
            <NH1 class="hero-title">
                <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
                    {{ dt('cookies.heroTitle') }}
                </NGradientText>
            </NH1>
            <NP class="hero-subtitle">{{ dt('cookies.heroSubtitle') }}</NP>
            <NP class="last-updated">{{ dt('cookies.lastUpdated') }}: {{ lastUpdated }}</NP>
        </div>

        <!-- Quick Summary -->
        <NAlert type="info" class="summary-alert">
            <template #icon>
                <Cookie />
            </template>
            <strong>{{ dt('cookies.summary.title') }}</strong>
            <NP style="margin-top: 8px;">{{ dt('cookies.summary.content') }}</NP>
        </NAlert>

        <!-- What Are Cookies -->
        <NCard class="content-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #36d1dc, #5b86e5);">
                    <Cookie size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('cookies.whatAre.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('cookies.whatAre.p1') }}</NP>
            <NP class="section-text">{{ dt('cookies.whatAre.p2') }}</NP>
            <NP class="section-text">{{ dt('cookies.whatAre.p3') }}</NP>
        </NCard>

        <!-- Cookie Categories & Preferences -->
        <NCard class="content-card preferences-card">
            <div class="section-header">
                <div class="section-icon" style="background: linear-gradient(135deg, #5b86e5, #ef473a);">
                    <Settings size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt('cookies.preferences.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('cookies.preferences.description') }}</NP>

            <div class="cookie-categories">
                <div v-for="category in cookieCategories" :key="category.key" class="category-item">
                    <div class="category-header">
                        <div class="category-icon" :style="{ backgroundColor: category.color }">
                            <component :is="category.icon" size="20" color="white" />
                        </div>
                        <div class="category-info">
                            <div class="category-name">{{ dt(category.name) }}</div>
                            <div class="category-description">{{ dt(category.description) }}</div>
                        </div>
                        <NSwitch 
                            v-model:value="cookiePreferences[category.key]" 
                            :disabled="!category.canDisable"
                            size="large"
                        />
                    </div>
                    <div class="category-examples">
                        <strong>{{ dt('cookies.preferences.examples') }}:</strong> {{ dt(category.examples) }}
                    </div>
                </div>
            </div>

            <NButton 
                type="primary" 
                size="large" 
                @click="saveCookiePreferences"
                class="save-preferences-button"
            >
                {{ dt('cookies.preferences.save') }}
            </NButton>
        </NCard>

        <!-- How We Use Cookies -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('cookies.howWeUse.title') }}</NH2>
            <NP class="section-text">{{ dt('cookies.howWeUse.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 6" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    <strong>{{ dt(`cookies.howWeUse.items.${i}.title`) }}:</strong>
                    {{ dt(`cookies.howWeUse.items.${i}.description`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Cookie Details Table -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('cookies.details.title') }}</NH2>
            <NP class="section-text">{{ dt('cookies.details.description') }}</NP>

            <div class="cookie-table-section">
                <NH3 class="subsection-title">{{ dt('cookies.details.necessary.title') }}</NH3>
                <div class="cookie-table">
                    <div class="table-row header-row">
                        <div class="table-cell">{{ dt('cookies.details.table.name') }}</div>
                        <div class="table-cell">{{ dt('cookies.details.table.purpose') }}</div>
                        <div class="table-cell">{{ dt('cookies.details.table.duration') }}</div>
                    </div>
                    <div v-for="i in 3" :key="i" class="table-row">
                        <div class="table-cell">{{ dt(`cookies.details.necessary.cookies.${i}.name`) }}</div>
                        <div class="table-cell">{{ dt(`cookies.details.necessary.cookies.${i}.purpose`) }}</div>
                        <div class="table-cell">{{ dt(`cookies.details.necessary.cookies.${i}.duration`) }}</div>
                    </div>
                </div>

                <NH3 class="subsection-title">{{ dt('cookies.details.functional.title') }}</NH3>
                <div class="cookie-table">
                    <div class="table-row header-row">
                        <div class="table-cell">{{ dt('cookies.details.table.name') }}</div>
                        <div class="table-cell">{{ dt('cookies.details.table.purpose') }}</div>
                        <div class="table-cell">{{ dt('cookies.details.table.duration') }}</div>
                    </div>
                    <div v-for="i in 2" :key="i" class="table-row">
                        <div class="table-cell">{{ dt(`cookies.details.functional.cookies.${i}.name`) }}</div>
                        <div class="table-cell">{{ dt(`cookies.details.functional.cookies.${i}.purpose`) }}</div>
                        <div class="table-cell">{{ dt(`cookies.details.functional.cookies.${i}.duration`) }}</div>
                    </div>
                </div>

                <NH3 class="subsection-title">{{ dt('cookies.details.analytics.title') }}</NH3>
                <div class="cookie-table">
                    <div class="table-row header-row">
                        <div class="table-cell">{{ dt('cookies.details.table.name') }}</div>
                        <div class="table-cell">{{ dt('cookies.details.table.purpose') }}</div>
                        <div class="table-cell">{{ dt('cookies.details.table.duration') }}</div>
                    </div>
                    <div v-for="i in 2" :key="i" class="table-row">
                        <div class="table-cell">{{ dt(`cookies.details.analytics.cookies.${i}.name`) }}</div>
                        <div class="table-cell">{{ dt(`cookies.details.analytics.cookies.${i}.purpose`) }}</div>
                        <div class="table-cell">{{ dt(`cookies.details.analytics.cookies.${i}.duration`) }}</div>
                    </div>
                </div>
            </div>
        </NCard>

        <!-- Third Party Cookies -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('cookies.thirdParty.title') }}</NH2>
            <NP class="section-text">{{ dt('cookies.thirdParty.p1') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 4" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    <strong>{{ dt(`cookies.thirdParty.services.${i}.name`) }}:</strong>
                    {{ dt(`cookies.thirdParty.services.${i}.description`) }}
                    <a :href="dt(`cookies.thirdParty.services.${i}.link`)" target="_blank" class="policy-link">
                        {{ dt('cookies.thirdParty.viewPolicy') }}
                    </a>
                </NListItem>
            </NList>
        </NCard>

        <!-- Managing Cookies -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('cookies.managing.title') }}</NH2>
            <NP class="section-text">{{ dt('cookies.managing.p1') }}</NP>
            <NP class="section-text">{{ dt('cookies.managing.p2') }}</NP>

            <NH3 class="subsection-title">{{ dt('cookies.managing.browsers.title') }}</NH3>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 5" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    <strong>{{ dt(`cookies.managing.browsers.${i}.name`) }}:</strong>
                    <a :href="dt(`cookies.managing.browsers.${i}.link`)" target="_blank" class="browser-link">
                        {{ dt('cookies.managing.browsers.instructions') }}
                    </a>
                </NListItem>
            </NList>

            <NAlert type="warning" style="margin-top: 20px;">
                {{ dt('cookies.managing.warning') }}
            </NAlert>
        </NCard>

        <!-- Changes to Policy -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('cookies.changes.title') }}</NH2>
            <NP class="section-text">{{ dt('cookies.changes.content') }}</NP>
        </NCard>

        <!-- Contact -->
        <NCard class="content-card contact-card">
            <NH2 class="section-title">{{ dt('cookies.contact.title') }}</NH2>
            <NP class="section-text">{{ dt('cookies.contact.content') }}</NP>
            <div class="contact-info">
                <NP><strong>{{ dt('cookies.contact.email') }}:</strong> privacy@lawdosti.com</NP>
            </div>
        </NCard>
    </div>
</template>

<style scoped>
.cookies-container {
    padding: 16px;
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    color: #333;
    overflow-x: hidden;
    box-sizing: border-box;
}

.hero-section {
    text-align: center;
    padding: 40px 0;
}

.hero-title {
    margin-bottom: 12px;
}

.hero-subtitle {
    font-size: 1.2rem;
    color: #555;
    margin-bottom: 8px;
}

.last-updated {
    font-size: 0.9rem;
    color: #888;
    font-style: italic;
}

.summary-alert {
    margin: 30px 0;
    border-radius: 12px;
}

.content-card {
    margin: 24px 0;
    border-radius: 16px;
}

.preferences-card {
    background: linear-gradient(135deg, rgba(54, 209, 220, 0.02), rgba(91, 134, 229, 0.02));
}

.section-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.section-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.section-title {
    margin-bottom: 16px;
    color: #333;
}

.subsection-title {
    margin: 24px 0 12px 0;
    color: #444;
}

.section-text {
    margin-bottom: 16px;
    line-height: 1.8;
    color: #555;
    font-size: 1rem;
}

.section-list {
    margin-top: 16px;
}

.list-bullet {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
}

.cookie-categories {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 24px 0;
}

.category-item {
    padding: 20px;
    background: white;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
}

.category-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
}

.category-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.category-info {
    flex: 1;
}

.category-name {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
}

.category-description {
    font-size: 0.9rem;
    color: #666;
}

.category-examples {
    font-size: 0.85rem;
    color: #777;
    padding-left: 60px;
}

.save-preferences-button {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
    border: none;
    margin-top: 20px;
}

.cookie-table-section {
    margin-top: 24px;
}

.cookie-table {
    margin: 16px 0 32px 0;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e8e8e8;
}

.table-row {
    display: grid;
    grid-template-columns: 2fr 3fr 1.5fr;
    gap: 0;
}

.header-row {
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
}

.header-row .table-cell {
    color: white;
    font-weight: 600;
}

.table-cell {
    padding: 12px 16px;
    border-right: 1px solid #e8e8e8;
    border-bottom: 1px solid #e8e8e8;
    font-size: 0.9rem;
}

.table-cell:last-child {
    border-right: none;
}

.table-row:last-child .table-cell {
    border-bottom: none;
}

.policy-link,
.browser-link {
    color: #36d1dc;
    text-decoration: none;
    margin-left: 8px;
}

.policy-link:hover,
.browser-link:hover {
    text-decoration: underline;
}

.contact-card {
    background: linear-gradient(135deg, rgba(54, 209, 220, 0.05), rgba(91, 134, 229, 0.05));
}

.contact-info {
    margin-top: 20px;
    padding: 20px;
    background: white;
    border-radius: 12px;
    border-left: 4px solid #36d1dc;
}

.contact-info p {
    margin: 8px 0;
}

@media screen and (max-width: 768px) {
    .hero-section {
        padding: 24px 0;
    }

    .section-header {
        flex-direction: column;
        text-align: center;
    }

    .section-icon {
        margin: 0 auto;
    }

    .category-header {
        flex-direction: column;
        text-align: center;
    }

    .category-examples {
        padding-left: 0;
        text-align: center;
    }

    .table-row {
        grid-template-columns: 1fr;
    }

    .table-cell {
        border-right: none;
    }
}
</style>